import { useState, useRef, useEffect } from 'react';
import { Pause, Play, Refresh, RefreshDouble, ThumbsDown, ThumbsUp } from 'iconoir-react';
import { getResponseAudio, sendFeedback } from '../services/feedback-service';
import { useTranslation } from 'react-i18next';
import { useChatStore } from '../../../state/store/chat/use-chat-store';
import { useConfig } from 'hooks/use-config';
import {
  CHAT_MESSAGE_ACTION_ICON_CLASS,
  CHAT_MESSAGE_ACTION_ICON_COMPACT_PROPS,
  CHAT_MESSAGE_ACTION_ICON_PROPS,
} from 'constant/chat-layout';
import { useToast } from '../../../hooks/use-toast';
import { MessageGeneration } from '../types/chat';
import { trackEvent } from 'analytics/tracker';
import { EVENTS, FeatureName } from 'analytics/events';
import { PipelineAnalytics } from './PipelineAnalytics';
import { MessageSkillEvents } from './MessageSkillEvents';
import { MessageSubagentRuns } from './MessageSubagentRuns';
import { MessageToolCalls } from './MessageToolCalls';

type FeedbackProps = {
  message: MessageGeneration;
  recordId: string;
  isStillGenerating: boolean;
  hideRegenerateButton: boolean;
  hidePipelineAnalytics?: boolean;
  hasParticipants?: boolean;
  /** When any assistant reply is generating in this conversation — hide regenerate on completed bubbles. */
  conversationResponseGenerating?: boolean;
  agentModelLabel?: string | null;
};

export function Feedback({
  message,
  recordId,
  isStillGenerating,
  hideRegenerateButton,
  hidePipelineAnalytics = false,
  hasParticipants,
  conversationResponseGenerating = false,
  agentModelLabel,
}: FeedbackProps) {

  const conversationId = useChatStore((state) => state.activeConversationId);
  const activeAudioMessageId = useChatStore((state) => state.activeAudioMessageId);
  const setActiveAudioMessageId = useChatStore((state) => state.setActiveAudioMessageId);
  const updateMessageFeedback = useChatStore((state) => state.updateMessageFeedback);
  const regenerateResponse = useChatStore((state) => state.regenerateResponse);
  const [liked, setLiked] = useState<boolean | undefined>(message.feedback);
  const { t } = useTranslation();
  const { configurationSettings } = useConfig();
  const iconProps = CHAT_MESSAGE_ACTION_ICON_PROPS;
  const { toast } = useToast();
  const [isAudioLoading, setIsAudioLoading] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  // MediaSource is unavailable on some iPhone browsers. Never construct it while rendering.
  const mediaSourceRef = useRef<MediaSource | null>(null);
  const audioRef = useRef<HTMLAudioElement>(new Audio());
  const cachedAudioBlobRef = useRef<Blob | null>(null);
  const isAudioRequestInFlightRef = useRef(false);
  const handleSourceOpenRef = useRef<(() => void) | null>(null);
  const canPlayRef = useRef<(() => void) | null>(null);
  const endedRef = useRef<(() => void) | null>(null);
  const errorRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    setLiked(message.feedback);

    cleanupAudio();
    if (cachedAudioBlobRef.current) cachedAudioBlobRef.current = null;
    audioRef.current = new Audio();
  }, [message.id]);


  const cleanupAudio = () => {
    if (audioRef.current) {
      const audio = audioRef.current;
      if (canPlayRef.current) audio.removeEventListener('canplay', canPlayRef.current);
      if (endedRef.current) audio.removeEventListener('ended', endedRef.current);
      if (errorRef.current) audio.removeEventListener('error', errorRef.current);

      if (audio.src) {
        URL.revokeObjectURL(audio.src);
      }
      audio.src = '';
      audio.pause();
      audio.load();
    }

    if (mediaSourceRef.current && mediaSourceRef.current.readyState === 'open') {
      try {
        mediaSourceRef.current.endOfStream();
      } catch (e) {
        console.warn('Failed to end media source stream:', e);
      }
    }
    mediaSourceRef.current?.removeEventListener('sourceopen', handleSourceOpenRef.current!);
    mediaSourceRef.current = null;
    isAudioRequestInFlightRef.current = false;
    setIsPlaying(false);
    setIsAudioLoading(false);
  };

  const handleThumbsClick = async (messageId: string, isLiked: boolean) => {
    if (!conversationId) return;
    if (isLiked === liked) return;

    const prevLiked = liked;

    try {
      setLiked(isLiked);
      await sendFeedback(
        {
          conversation_id: conversationId,
          msg_id: messageId,
          feedback: isLiked,
        },
        configurationSettings
      );
      updateMessageFeedback(recordId, messageId, isLiked);
    } catch (error) {
      setLiked(prevLiked);
      console.error('Failed to send feedback:', error);
      toast({
        variant: 'destructive',
        title: t('FAILED_TO_SEND_FEEDBACK'),
        description: t('FAILED_TO_SEND_FEEDBACK_DESCRIPTION'),
      });
    }
  };

  const handleRegenerateClick = () => {
    // console.log('recordId in handleRegenerateClick', recordId)
    // if (!conversationId) return;
    // const currentConversation = useChatStore.getState().conversations[conversationId];
    // currentConversation
    if (!recordId) return;
    regenerateResponse(recordId, configurationSettings);
  };

  const handlePlayClick = async (messageId: string) => {
    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
      setActiveAudioMessageId(null);
      return;
    }

    if (isAudioLoading || isAudioRequestInFlightRef.current) {
      return;
    }

    // If there's another audio playing, stop it first
    if (activeAudioMessageId && activeAudioMessageId !== messageId) {
      setActiveAudioMessageId(null);
    }

    // Set this message as the active audio
    setActiveAudioMessageId(messageId);

    try {
      if (cachedAudioBlobRef.current) {
        const audio = new Audio(URL.createObjectURL(cachedAudioBlobRef.current));
        audioRef.current = audio;
        await audio.play();
        setIsPlaying(true);

        audio.addEventListener('ended', () => {
          setIsPlaying(false);
          setActiveAudioMessageId(null);
        });
        return;
      }

      trackEvent(EVENTS.FEATURE_USED(FeatureName.READ_ALOUD));

      isAudioRequestInFlightRef.current = true;
      setIsAudioLoading(true);
      const canStreamAudio =
        typeof MediaSource !== 'undefined' && MediaSource.isTypeSupported('audio/mpeg');

      // Use native MP3 playback when MSE or its MP3 SourceBuffer is unsupported.
      if (!canStreamAudio) {
        const audio = audioRef.current;
        const response = await getResponseAudio({
          conversation_id: conversationId!,
          msg_id: messageId,
        }, configurationSettings);
        const blob = await response.blob();
        if (audioRef.current !== audio || !isAudioRequestInFlightRef.current) return;
        cachedAudioBlobRef.current = blob;
        audio.src = URL.createObjectURL(blob);
        endedRef.current = () => {
          setIsPlaying(false);
          setActiveAudioMessageId(null);
        };
        audio.addEventListener('ended', endedRef.current);
        await audio.play();
        isAudioRequestInFlightRef.current = false;
        setIsAudioLoading(false);
        setIsPlaying(true);
        return;
      }

      mediaSourceRef.current = new MediaSource();
      const response = getResponseAudio({
        conversation_id: conversationId!,
        msg_id: messageId,
      }, configurationSettings);


      audioRef.current.src = URL.createObjectURL(mediaSourceRef.current);

      canPlayRef.current = () => {
        audioRef.current.play().catch(error => {
          console.error('Error playing audio:', error);
          setIsPlaying(false);
          isAudioRequestInFlightRef.current = false;
          setIsAudioLoading(false);
          setActiveAudioMessageId(null);
        });
        setIsPlaying(true);
        isAudioRequestInFlightRef.current = false;
        setIsAudioLoading(false);
      };

      endedRef.current = () => {
        setIsPlaying(false);
        setActiveAudioMessageId(null);
      };

      errorRef.current = () => {
        setIsPlaying(false);
        isAudioRequestInFlightRef.current = false;
        setIsAudioLoading(false);
        setActiveAudioMessageId(null);
      };

      audioRef.current.addEventListener('canplay', canPlayRef.current);
      audioRef.current.addEventListener('ended', endedRef.current);
      audioRef.current.addEventListener('error', errorRef.current);

      const handleSourceOpen = async () => {
        if (!mediaSourceRef.current) return;

        try {
          const mime = 'audio/mpeg';
          const sourceBuffer = mediaSourceRef.current.addSourceBuffer(mime);

          if (!response) {
            console.warn('Response not available');
            isAudioRequestInFlightRef.current = false;
            setIsAudioLoading(false);
            setActiveAudioMessageId(null);
            return;
          }

          let _response;
          try {
            _response = await response;
          } catch (error) {
            isAudioRequestInFlightRef.current = false;
            setIsAudioLoading(false);
            setActiveAudioMessageId(null);
            toast({
              title: t('STREAMING_ERROR'),
              description: t('FAILED_TO_STREAM_AUDIO'),
              variant: 'destructive',
            });
            return;
          }

          if (!sourceBuffer) {
            isAudioRequestInFlightRef.current = false;
            setIsAudioLoading(false);
            setActiveAudioMessageId(null);
            console.warn('Source buffer not available');
            return;
          }

          if (!_response.body) {
            isAudioRequestInFlightRef.current = false;
            setIsAudioLoading(false);
            setActiveAudioMessageId(null);
            console.warn('Response body not available');
            return;
          }

          const reader = _response.body.getReader();
          const chunks: BlobPart[] = [];
          /// Previous: const chunks: Uint8Array[] = [];


          const pump = async () => {
            if (!mediaSourceRef.current || !sourceBuffer) {
              return;
            }

            const { done, value } = await reader.read();

            if (done) {
              if (mediaSourceRef.current?.readyState === 'open') {
                mediaSourceRef.current.endOfStream();
              }
              const blob = new Blob(chunks, { type: 'audio/mpeg' });
              cachedAudioBlobRef.current = blob;
              return;
            }

            chunks.push(value!.slice());
            /// Previous: chunks.push(value);

            if (!sourceBuffer) {
              return;
            }

            if (!sourceBuffer.updating) {
              if (mediaSourceRef.current?.readyState === 'open') {
                sourceBuffer.appendBuffer(value!);
              } else {
                return;
              }
            } else {
              const onUpdate = () => {
                if (!sourceBuffer) {
                  return;
                }
                sourceBuffer.removeEventListener('updateend', onUpdate);
                sourceBuffer.appendBuffer(value!);
              };
              sourceBuffer.addEventListener('updateend', onUpdate);
            }

            if (sourceBuffer) {
              sourceBuffer.addEventListener('updateend', pump, { once: true });
            }
          };

          pump();
        } catch (error) {
          console.error('Streaming error:', error);
          if (mediaSourceRef.current?.readyState === 'open') {
            try {
              mediaSourceRef.current.endOfStream();
            } catch (e) {
              console.warn('Failed to end media source stream:', e);
            }
          }
          isAudioRequestInFlightRef.current = false;
          setIsAudioLoading(false);
          setActiveAudioMessageId(null);
        }
      };

      handleSourceOpenRef.current = handleSourceOpen;
      mediaSourceRef.current.addEventListener('sourceopen', handleSourceOpen);
    } catch (error) {
      isAudioRequestInFlightRef.current = false;
      setIsAudioLoading(false);
      setActiveAudioMessageId(null);
      toast({
        title: t('STREAMING_ERROR'),
        description: t('FAILED_TO_STREAM_AUDIO'),
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    return () => {
      cleanupAudio();
    }
  }, []);

  useEffect(() => {
    if (activeAudioMessageId && activeAudioMessageId !== message.id) {
      cleanupAudio();
    }
  }, [activeAudioMessageId, message.id]);

  if (message.isCancelled) return null;

  return (
    <div className="flex gap-1 justify-start">
      {!isStillGenerating && (
        <>
          <button
            title={t('GOOD_RESPONSE')}
            className="inline-flex shrink-0 size-8 items-center justify-center rounded-full p-0 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => handleThumbsClick(message.id, true)}
          >
            <ThumbsUp
              {...iconProps}
              className={`${CHAT_MESSAGE_ACTION_ICON_CLASS} ${
                liked !== undefined && liked ? 'fill-[#B0B0B0]' : 'fill-none'
              }`}
            />
          </button>

          <button
            title={t('BAD_RESPONSE')}
            className="inline-flex shrink-0 size-8 items-center justify-center rounded-full p-0 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => handleThumbsClick(message.id, false)}
          >
            <ThumbsDown
              {...iconProps}
              className={`${CHAT_MESSAGE_ACTION_ICON_CLASS} ${
                liked !== undefined && !liked ? 'fill-[#B0B0B0]' : 'fill-none'
              }`}
            />
          </button>

          {!message.HasFileGeneratedResponse && (
            <button
              title={t('READ_ALOUD')}
              className="inline-flex shrink-0 size-8 items-center justify-center rounded-full p-0 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => handlePlayClick(message.id)}
              disabled={isAudioLoading}
            >
              {isAudioLoading ? (
                <Refresh {...iconProps} className={`${CHAT_MESSAGE_ACTION_ICON_CLASS} animate-spin`} />
              ) : isPlaying ? (
                <Pause {...iconProps} />
              ) : (
                <Play {...iconProps} />
              )}
            </button>
          )}

          {!hideRegenerateButton &&
            !hasParticipants &&
            !conversationResponseGenerating && (
              <button
                title={'Regenerate'}
                className="inline-flex shrink-0 size-8 items-center justify-center rounded-full p-0 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleRegenerateClick}
              >
                <RefreshDouble {...CHAT_MESSAGE_ACTION_ICON_COMPACT_PROPS} />
              </button>
            )}
        </>
      )}

      {!isStillGenerating && conversationId && message.skillEvents && message.skillEvents.length > 0 && (
        <MessageSkillEvents message={message} />
      )}
      {!isStillGenerating && conversationId && message.toolCalls && message.toolCalls.length > 0 && (
        <MessageToolCalls message={message} />
      )}
      {!isStillGenerating && conversationId && message.subagentRuns && message.subagentRuns.length > 0 && (
        <MessageSubagentRuns message={message} />
      )}
      {conversationId && !isStillGenerating && !hidePipelineAnalytics && (
        <PipelineAnalytics
          message={message}
          conversationId={conversationId}
          recordId={recordId}
          agentModelLabel={agentModelLabel}
        />
      )}
    </div>
  );
}
